/**
 * HMAC-SHA256 request signing.
 *
 * signingString = METHOD \n PATH(+raw query) \n TIMESTAMP \n NONCE \n RAW_BODY
 * RAW_BODY must be the exact text sent on the wire ('' when there is none).
 */

const ENABLE_SIGNING = import.meta.env.VITE_ENABLE_SIGNING === 'true';
const SECRET = import.meta.env.VITE_API_HMAC_SECRET || '';

export const isSigningEnabled = () =>
  ENABLE_SIGNING && SECRET.length >= 32 && !!globalThis.crypto?.subtle;

const enc = new TextEncoder();

let hmacKey: Promise<CryptoKey> | null = null;
const getHmacKey = () =>
  (hmacKey ??= crypto.subtle.importKey(
    'raw',
    enc.encode(SECRET),
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign']
  ));

const toHex = (buf: ArrayBuffer) =>
  [...new Uint8Array(buf)].map((b) => b.toString(16).padStart(2, '0')).join('');

/** Returns the X-Timestamp / X-Nonce / X-Signature headers for one request. */
export async function signRequest(method: string, path: string, rawBody: string) {
  const timestamp = String(Date.now());
  const nonce = crypto.randomUUID();
  const signingString = `${method.toUpperCase()}\n${path}\n${timestamp}\n${nonce}\n${rawBody}`;
  const sig = await crypto.subtle.sign('HMAC', await getHmacKey(), enc.encode(signingString));
  return { 'X-Timestamp': timestamp, 'X-Nonce': nonce, 'X-Signature': toHex(sig) };
}

const SIGNING_ERROR_RE = /request signature|timestamp expired|nonce/i;

/** True for a 401 caused by HMAC signing (not an expired session), so callers must not log out. */
export const isSigningError = (error: unknown) => {
  const message = (error as { data?: { message?: unknown } } | undefined)?.data?.message;
  return typeof message === 'string' && SIGNING_ERROR_RE.test(message);
};
